import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Avatar from "react-avatar";
import { Dropdown } from "react-bootstrap";
import DeleteModal from "../../../../common/DeleteModal";

/**
 * @typedef {Object} MemberTableRowProps
 * @property {Object} member - The member object
 * @property {(memberId: string) => Promise<void>} onDelete - Function to delete a member
 * @property {boolean} deleteLoading - Whether delete is in progress
 * @property {string} deleteError - Error message from delete operation
 * @property {boolean} deleteSuccess - Whether delete was successful
 * @property {() => void} resetDeleteState - Function to reset delete state
 * @property {() => void} onDeleteSuccess - Callback after successful deletion
 * @property {(memberId: string) => Promise<void>} onToggleStatus - Function to toggle member status
 * @property {boolean} toggleLoading - Whether toggle is in progress
 * @property {string} toggleError - Error message from toggle operation
 * @property {boolean} toggleSuccess - Whether toggle was successful
 * @property {string} toggleStatusMessage - Status message after toggle
 * @property {() => void} resetToggleState - Function to reset toggle state
 * @property {() => void} onToggleSuccess - Callback after successful toggle
 */

/**
 * @param {MemberTableRowProps} props
 */
const MemberTableRow = ({
  member,
  onDelete,
  deleteLoading,
  deleteError,
  deleteSuccess,
  resetDeleteState,
  onDeleteSuccess,
  onToggleStatus,
  toggleLoading,
  toggleError,
  toggleSuccess,
  toggleStatusMessage,
  resetToggleState,
  onToggleSuccess,
}) => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(""); // "delete" | "toggle"
  const [isProcessing, setIsProcessing] = useState(false);

  // Combine firstName and lastName for display
  const fullName = [member.firstName, member.lastName]
    .filter(Boolean)
    .join(" ");

  // Get role name from role object or use role directly if string
  const roleName =
    typeof member.role === "object" ? member.role?.name : member.role;

  // Check if member is active
  const isActive = member.status === "1" || member.status === 1;

  const handleEdit = () => {
    navigate(`/school-admin/settings/members/edit/${member.id}`);
  };

  // Show modal handlers
  const handleShowDelete = () => {
    resetDeleteState();
    setModalType("delete");
    setShowModal(true);
  };

  const handleShowToggle = () => {
    resetToggleState();
    setModalType("toggle");
    setShowModal(true);
  };

  const handleClose = () => {
    if (modalType === "delete") {
      resetDeleteState();
    } else {
      resetToggleState();
    }
    setShowModal(false);
    setIsProcessing(false);
  };

  const handleAction = async () => {
    setIsProcessing(true);
    if (modalType === "delete") {
      await onDelete(member.id);
    } else {
      await onToggleStatus(member.id);
    }
  };

  const handleSuccessContinue = () => {
    setShowModal(false);
    setIsProcessing(false);
    if (modalType === "delete") {
      resetDeleteState();
      onDeleteSuccess();
    } else {
      resetToggleState();
      onToggleSuccess();
    }
  };

  // Get current modal state based on type
  const currentLoading = modalType === "delete" ? deleteLoading : toggleLoading;
  const currentError = modalType === "delete" ? deleteError : toggleError;
  const currentSuccess = modalType === "delete" ? deleteSuccess : toggleSuccess;

  // Only show states for this specific row
  const showLoading = isProcessing && currentLoading;
  const showError = isProcessing && currentError;
  const showSuccess = isProcessing && currentSuccess;

  // Get modal content based on type
  const getModalMessage = () => {
    if (modalType === "delete") {
      return `Are you sure you want to delete ${fullName}? This action cannot be undone.`;
    }
    return `Are you sure you want to ${isActive ? "disable" : "enable"} ${fullName}?`;
  };

  const getSuccessMessage = () => {
    if (modalType === "delete") {
      return `${fullName} has been deleted successfully.`;
    }
    return toggleStatusMessage || `${fullName} has been ${isActive ? "disabled" : "enabled"} successfully.`;
  };

  return (
    <tr>
      <td className="member-col-name">
        <div className="d-flex align-items-center gap-2 gap-sm-3">
          <div className="flex-shrink-0">
            <Avatar
              name={fullName}
              round="50%"
              size="38"
              color="#E0F7FA"
              fgColor="#00838F"
            />
          </div>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontWeight: "500",
                color: "var(--text-color)",
                whiteSpace: "nowrap",
              }}
            >
              {fullName}
            </div>
            <div
              style={{
                fontSize: "13px",
                color: "#6c757d",
                whiteSpace: "nowrap",
              }}
            >
              {member.email}
            </div>
          </div>
        </div>
      </td>
      <td className="member-col-role" style={{ verticalAlign: "middle" }}>
        <span style={{ color: "var(--text-color)", whiteSpace: "nowrap" }}>{roleName}</span>
      </td>
      <td className="member-col-status text-center" style={{ verticalAlign: "middle" }}>
        {isActive ? (
          <span className="badge bg-success">Active</span>
        ) : (
          <span className="badge bg-secondary">Inactive</span>
        )}
      </td>
      <td className="member-col-action text-center" style={{ verticalAlign: "middle" }}>
        <Dropdown>
          <Dropdown.Toggle
            as="button"
            bsPrefix="dropdown-toggle-no-caret"
            className="btn btn-link border-0 p-2"
            style={{
              color: "#6c757d",
              fontSize: "18px",
              background: "none",
              boxShadow: "none",
            }}
          >
            <i className="fa-solid fa-ellipsis-vertical"></i>
          </Dropdown.Toggle>
          <Dropdown.Menu
            align="end"
            renderOnMount
            popperConfig={{ strategy: "fixed" }}
          >
            <Dropdown.Item onClick={handleEdit}>
              <i
                className="fa-solid fa-pen me-2"
                style={{ color: "#0d6efd" }}
              ></i>
              Edit
            </Dropdown.Item>
            <Dropdown.Item onClick={handleShowToggle}>
              <i
                className={`fa-solid ${isActive ? "fa-ban" : "fa-check"} me-2`}
                style={{ color: isActive ? "#ffc107" : "#198754" }}
              ></i>
              {isActive ? "Disable" : "Enable"}
            </Dropdown.Item>
            <Dropdown.Item className="text-danger" onClick={handleShowDelete}>
              <i className="fa-solid fa-trash me-2"></i>
              Delete
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>

        <DeleteModal
          showModal={showModal}
          handleClose={handleClose}
          message={getModalMessage()}
          loading={showLoading}
          handleDelete={handleAction}
          error={showError ? currentError : ""}
          success={showSuccess}
          successMessage={getSuccessMessage()}
          onSuccessContinue={handleSuccessContinue}
          customConfirmTitle={modalType === "delete" ? "Confirm Delete" : "Confirm Toggle"}
          customSuccessTitle={modalType === "delete" ? "Deleted Successfully" : "Toggled Successfully"}
          customErrorTitle={modalType === "delete" ? "Delete Failed" : "Toggle Failed"}
          isToggle={modalType === "toggle"}
          isActive={isActive}
        />
      </td>
    </tr>
  );
};

export default MemberTableRow;
